'use client';

import { Globe } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { useI18n } from '@/components/i18n-provider';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { apiClient } from '@/lib/api-client';
import { LOCALE_NAMES, type Locale, locales } from '@/lib/i18n';

/**
 * Language choice, stored in a cookie.
 *
 * `router.refresh()` after storing it, because every string on these screens is
 * chosen during the server render — re-rendering on the client alone would
 * leave the page in the old language until the next navigation.
 *
 * The menu is Radix rather than the hand-written one this replaced. That
 * version tracked its own outside-click and Escape handlers but had no arrow-key
 * navigation, no type-ahead, and no focus return to the trigger on close —
 * three things a keyboard user needs and nobody notices are missing until they
 * are the one using it.
 */
export function LanguageSwitcher() {
  const { locale } = useI18n();
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  const choose = async (next: string) => {
    if (next === locale) return;

    await apiClient.setLocale(next as Locale);
    startTransition(() => router.refresh());
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          shape="pill"
          size="sm"
          className="h-9 gap-1.5 px-2.5"
          disabled={pending}
          aria-label={LOCALE_NAMES[locale]}
        >
          <Globe />
          <span className="type-caption font-semibold uppercase">{locale}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={locale} onValueChange={choose}>
          {locales.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {/* Fixed-width code column, so the language names below it start
                  on the same x-position rather than stepping in and out. */}
              <span className="type-caption w-6 shrink-0 font-semibold text-foreground-faint uppercase">
                {option}
              </span>
              <span className="flex-1">{LOCALE_NAMES[option]}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
